// src/pages/pengamatan/sensus-populasi-anakan-pisang/SensusPopulasiAnakanPisangToolbar.jsx
import React, { useState } from 'react';
import {
    Typography, Stack, TextField, IconButton, Menu, MenuItem, Divider,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Button, Alert, Snackbar
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import DownloadIcon from "@mui/icons-material/Download";
import { styled, alpha } from '@mui/material/styles';

// Import fungsi-fungsi terpisah
import { useDraftActions } from './draftActions';
import { handleDownloadExcel } from './excelDownload';
import { usePdfDownload, PdfDownloadDialog } from './pdfDownload';

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

// Transition component untuk dialog konfirmasi
const Transition = React.forwardRef(function Transition(props, ref) {
  const { Slide } = require("@mui/material");
  return <Slide direction="up" ref={ref} {...props} />;
});

const SensusPopulasiAnakanPisangToolbar = ({ 
  searchTerm, 
  onSearchChange, 
  onResetFilter, 
  anchorEl, 
  onMenuClick, 
  onMenuClose, 
  open,
  selectedItems,
  userJabatan,
  userRoleNames,
  updateStatusPengamatan,
  onStatusUpdate,
  onReload,
  orders,
  // Props baru untuk filter control
  isFilterOpen,
  onToggleFilter,
  onApplyFilter,
  filterCount
}) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Gunakan hook untuk aksi draft dengan semua fungsi terkait status
  const {
    confirmDialogOpen,
    dialogAction,
    loading,
    // Status checking functions
    allSelectedItemsAreDraft,
    allSelectedItemsAreProcessed,
    allSelectedItemsAreApproved,
    allSelectedItemsAreOnlyProcessed,
    hasMixedApprovedAndProcessed,
    // Menu display logic
    showDraftMenu,
    showProcessedMenu,
    showApprovedMenu,
    // Action handlers
    handleOpenDialog,
    handleCloseDialog,
    handleConfirmAction
  } = useDraftActions({
    selectedItems,
    orders,
    updateStatusPengamatan,
    onStatusUpdate,
    onReload,
    onMenuClose
  });

  // Gunakan hook untuk PDF download - DIPERBARUI dengan fungsi clear
  const {
    downloadDialogOpen,
    selectedLocation,
    locationInput,
    setSelectedLocation,
    setLocationInput,
    getProcessedLocations,
    handleOpenDownloadDialog,
    handleCloseDownloadDialog,
    handleDownloadPDF,
    handleLocationInputChange,
    handleInputChange,
    helpAnchorEl,
    handleHelpClick,
    handleHelpClose,
    handleClearInput,
    helpOpen
  } = usePdfDownload(orders, onStatusUpdate);

  // Fungsi untuk mengecek apakah user memiliki akses untuk mengubah status pengamatan
  const canEditStatus = () => {
    // Admin dengan role Sensus Populasi Anakan Pisang
    const isAdminWithValidRole = userJabatan === "Admin" && 
      (userRoleNames.includes("Sensus Populasi Anakan Pisang") ||
       userRoleNames.includes("Developer"));
    
    // Section Head dengan role Developer
    const isSectionHeadDeveloper = userJabatan === "Section Head" && 
      userRoleNames.includes("Developer");
    
    return isAdminWithValidRole || isSectionHeadDeveloper;
  };

  // Wrapper function untuk download Excel
  const handleExcelDownload = () => {
    handleDownloadExcel(selectedItems, orders, onStatusUpdate, onMenuClose);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handler untuk tombol filter
  const handleFilterClick = () => {
    onToggleFilter();
  };

  // Handler untuk apply filter
  const handleApplyFilter = () => {
    onApplyFilter();
    setSnackbar({
      open: true,
      message: 'Filter berhasil diterapkan',
      severity: 'success'
    });
  };

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" component="h2">
          Data Sensus Populasi Anakan Pisang
          {filterCount > 0 && (
            <Typography 
              component="span" 
              variant="body2" 
              sx={{ 
                ml: 1, 
                color: 'primary.main',
                fontWeight: 'bold'
              }}
            >
              ({filterCount} filter aktif)
            </Typography>
          )}
        </Typography>
        
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            placeholder="Cari berdasarkan block..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={onSearchChange}
            sx={{ minWidth: 300 }}
          />
          
          <IconButton 
            onClick={handleFilterClick} 
            color={isFilterOpen ? "primary" : "default"}
            title={isFilterOpen ? "Sembunyikan filter" : "Tampilkan filter"}
          >
            {isFilterOpen ? <FilterAltOutlinedIcon /> : <FilterAltOffIcon />}
          </IconButton>

          {/* Tombol Download Baru */}
          <IconButton
            onClick={handleOpenDownloadDialog}
            color="primary"
            title="Download Form"
          >
            <DownloadIcon />
          </IconButton>

          {canEditStatus() && (
            <IconButton
              onClick={onMenuClick}
              color="primary"
              disabled={selectedItems.length === 0}
              title="Aksi"
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Dialog Download Form PDF - DIPERBARUI dengan props clear */}
      <PdfDownloadDialog
        open={downloadDialogOpen}
        selectedLocation={selectedLocation}
        locationInput={locationInput}
        onClose={handleCloseDownloadDialog}
        onLocationChange={handleLocationInputChange}
        onInputChange={handleInputChange}
        onDownload={handleDownloadPDF}
        processedLocations={getProcessedLocations()}
        helpAnchorEl={helpAnchorEl}
        onHelpClick={handleHelpClick}
        onHelpClose={handleHelpClose}
        onClearInput={handleClearInput}
        helpOpen={helpOpen}
      />

      {/* Menu Aksi */}
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={onMenuClose}
      >
        {showDraftMenu() && (
          <MenuItem onClick={() => handleOpenDialog('Draft')} disableRipple>
            <EditIcon />
            Ubah Ke Draft
          </MenuItem>
        )}
        
        {showProcessedMenu() && (
          <MenuItem onClick={() => handleOpenDialog('Processed')} disableRipple>
            <EditIcon />
            Ubah Ke Processed
          </MenuItem>
        )}
        
        {showApprovedMenu() && (
          <MenuItem onClick={() => handleOpenDialog('Approved')} disableRipple>
            <ArchiveIcon />
            Ubah Ke Approved
          </MenuItem>
        )}

        {/* Menu Download Entry */}
        <MenuItem 
          onClick={handleExcelDownload} 
          disableRipple
          disabled={!allSelectedItemsAreOnlyProcessed()}
        >
          <DownloadIcon />
          Download Entry
        </MenuItem>
        
        <Divider sx={{ my: 0.5 }} />
        
        <MenuItem onClick={onMenuClose} disableRipple>
          Batal
        </MenuItem>
      </StyledMenu>

      {/* Dialog Konfirmasi Status */}
      <Dialog
        open={confirmDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseDialog}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>Konfirmasi Perubahan Status</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            Anda yakin ingin mengubah status {selectedItems.length} item menjadi "{dialogAction}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>Batal</Button>
          <Button 
            onClick={handleConfirmAction} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'Memproses...' : 'Ya, Ubah Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar untuk feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SensusPopulasiAnakanPisangToolbar;